/**
 * Which renderer shows a document. Pure, so `node --test` covers it.
 *
 * The content type wins over the extension: versions carry `contentType` from the upload, and it
 * is right even when a file name has no extension or the wrong one. List endpoints leave it out,
 * so the extension is the fallback. TIFF is deliberately absent: Chrome and Edge cannot draw it,
 * so it gets the download card instead of a broken image.
 */

export const DOCUMENT_KIND = Object.freeze({
  PDF: 'pdf',
  IMAGE: 'image',
  OFFICE: 'office',
  TEXT: 'text',
  CSV: 'csv',
  JSON: 'json',
  XML: 'xml',
  VIDEO: 'video',
  AUDIO: 'audio',
});

const EXTENSIONS = Object.freeze({
  [DOCUMENT_KIND.PDF]: ['pdf'],
  [DOCUMENT_KIND.IMAGE]: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'apng', 'avif'],
  [DOCUMENT_KIND.OFFICE]: [
    'doc', 'docx', 'xls', 'xlsx', 'xlsm', 'ppt', 'pptx', 'pps', 'ppsx',
    'rtf', 'odt', 'ods', 'odp', 'html', 'htm', 'xps', 'pub', 'vsd', 'vsdx', 'mpp',
  ],
  [DOCUMENT_KIND.TEXT]: [
    'txt', 'log', 'md', 'ini', 'cfg', 'conf', 'yaml', 'yml', 'toml', 'sql', 'properties', 'env',
    'js', 'ts', 'py', 'cs', 'java', 'c', 'cpp', 'h', 'sh', 'bat', 'ps1', 'css', 'scss',
  ],
  [DOCUMENT_KIND.CSV]: ['csv', 'tsv'],
  [DOCUMENT_KIND.JSON]: ['json', 'geojson'],
  [DOCUMENT_KIND.XML]: ['xml', 'kml', 'gpx'],
  [DOCUMENT_KIND.VIDEO]: ['mp4', 'm4v', 'webm', 'ogv', 'mov'],
  [DOCUMENT_KIND.AUDIO]: ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac'],
});

const EXTENSION_TO_KIND = Object.freeze(
  Object.entries(EXTENSIONS).reduce((map, [kind, extensions]) => {
    extensions.forEach((ext) => (map[ext] = kind));
    return map;
  }, {}),
);

const EXACT_CONTENT_TYPES = Object.freeze({
  'application/pdf': DOCUMENT_KIND.PDF,
  'text/csv': DOCUMENT_KIND.CSV,
  'text/tab-separated-values': DOCUMENT_KIND.CSV,
  'application/json': DOCUMENT_KIND.JSON,
  'application/geo+json': DOCUMENT_KIND.JSON,
  'application/xml': DOCUMENT_KIND.XML,
  'text/xml': DOCUMENT_KIND.XML,
  'application/msword': DOCUMENT_KIND.OFFICE,
  'application/rtf': DOCUMENT_KIND.OFFICE,
  'image/tiff': null,
});

/** Content types that say nothing about the file; the extension decides. */
const GENERIC_CONTENT_TYPES = ['application/octet-stream', 'binary/octet-stream', 'application/x-www-form-urlencoded'];

/** Text-family files are read into memory; anything bigger is cut, with a notice. */
export const TEXT_MAX_BYTES = 5 * 1024 * 1024;
/** Delimited files may be bigger: the table is virtualised. */
export const CSV_MAX_BYTES = 20 * 1024 * 1024;
export const CSV_MAX_ROWS = 100000;
/** An older version streams through the API as one blob; ask before pulling more than this. */
export const MEDIA_CONFIRM_BYTES = 200 * 1024 * 1024;

/** Lower-case extension of the document's file name ('' when it has none). */
export function documentExtension(document) {
  const name = String(document?.fileName || document?.documentNumber || document?.name || '');
  const base = name.split(/[?#]/)[0].split('/').pop();
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

function kindFromContentType(contentType) {
  const type = String(contentType || '').split(';')[0].trim().toLowerCase();
  if (!type || GENERIC_CONTENT_TYPES.includes(type)) return undefined;
  if (type in EXACT_CONTENT_TYPES) return EXACT_CONTENT_TYPES[type];
  if (type.startsWith('application/vnd.openxmlformats-officedocument.')) return DOCUMENT_KIND.OFFICE;
  if (type.startsWith('application/vnd.ms-')) return DOCUMENT_KIND.OFFICE;
  if (type.startsWith('application/vnd.oasis.opendocument.')) return DOCUMENT_KIND.OFFICE;
  return undefined;
}

/** Broad families, used only when neither the exact type nor the extension decided. */
function kindFromContentFamily(contentType) {
  const type = String(contentType || '').split(';')[0].trim().toLowerCase();
  if (type.startsWith('image/')) return DOCUMENT_KIND.IMAGE;
  if (type.startsWith('video/')) return DOCUMENT_KIND.VIDEO;
  if (type.startsWith('audio/')) return DOCUMENT_KIND.AUDIO;
  if (type.startsWith('text/') && type !== 'text/html') return DOCUMENT_KIND.TEXT;
  return null;
}

/**
 * The renderer kind for a document, or null when nothing in the app can show it.
 * @param {{ fileName?: string, documentNumber?: string, contentType?: string }} document
 * @returns {string|null} one of DOCUMENT_KIND
 */
export function resolveKind(document) {
  if (!document) return null;
  const byType = kindFromContentType(document.contentType);
  if (byType !== undefined) return byType;
  const ext = documentExtension(document);
  if (ext && EXTENSION_TO_KIND[ext]) return EXTENSION_TO_KIND[ext];
  if (ext === 'tif' || ext === 'tiff') return null;
  return kindFromContentFamily(document.contentType);
}

const KIND_ICONS = Object.freeze({
  [DOCUMENT_KIND.PDF]: 'mdi-file-pdf-box',
  [DOCUMENT_KIND.IMAGE]: 'mdi-file-image-outline',
  [DOCUMENT_KIND.OFFICE]: 'mdi-file-word-box-outline',
  [DOCUMENT_KIND.TEXT]: 'mdi-file-document-outline',
  [DOCUMENT_KIND.CSV]: 'mdi-file-delimited-outline',
  [DOCUMENT_KIND.JSON]: 'mdi-code-json',
  [DOCUMENT_KIND.XML]: 'mdi-file-code-outline',
  [DOCUMENT_KIND.VIDEO]: 'mdi-file-video-outline',
  [DOCUMENT_KIND.AUDIO]: 'mdi-file-music-outline',
});

export function kindIcon(kind) {
  return KIND_ICONS[kind] || 'mdi-file-outline';
}

/** Stable key for "this file of this document", so a renderer remounts when either changes. */
export function documentRenderKey(document) {
  if (!document) return 'none';
  const id = document.referenceDocumentId || document.documentId || document.id || '';
  return [id, document.viewRevisionId || 'current', document.fileName || '', String(document.storageReference || '').split('?')[0]].join('|');
}
